"use client";

import React, { useState } from "react";
import HeaderMenuProps from "@/src/_types/header-menu-props";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Box, MenuItem, MenuList } from "@mui/material";
import {
  caretDownSx,
  FONT_TEXT,
  FONT_UI,
} from "@/src/components/common/broadsheet/tokens";
import { normalizeSitePath } from "@/src/utils/paths";

const itemSx = {
  fontFamily: FONT_TEXT,
  fontSize: "0.8125rem",
  fontWeight: 700,
  letterSpacing: "0.11em",
  textTransform: "uppercase" as const,
  lineHeight: 1.2,
  color: "custom.ink",
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  gap: 0.125,
  whiteSpace: "nowrap" as const,
  borderBottom: "2px solid transparent",
  px: 0.5,
  py: 0.75,
  "&:hover": { color: "custom.link" },
};

const DesktopMenu = ({ menuItems }: HeaderMenuProps) => {
  const pathname = normalizeSitePath(usePathname());
  const [open, setOpen] = useState<{ index: number; path: string } | null>(
    null,
  );

  const openIndex = open && open.path === pathname ? open.index : null;

  const isCurrent = (link?: string) =>
    !!link && normalizeSitePath(link) === pathname;

  const isWithin = (link?: string) => {
    if (!link) return false;
    const target = normalizeSitePath(link);
    if (target === "/") return pathname === "/";
    return pathname === target || pathname.startsWith(`${target}/`);
  };

  return (
    <>
      {menuItems?.map((item, index) => {
        const active =
          isWithin(item.link) ||
          (item.children ?? []).some((child) => isWithin(child.link));

        return (
          <Box
            key={index}
            onMouseEnter={() =>
              item.children && setOpen({ index, path: pathname })
            }
            onMouseLeave={() => setOpen(null)}
            onFocus={() => item.children && setOpen({ index, path: pathname })}
            onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                setOpen(null);
              }
            }}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === "Escape") setOpen(null);
            }}
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              component={Link}
              href={item.link || "#"}
              onClick={() => setOpen(null)}
              aria-current={isCurrent(item.link) ? "page" : undefined}
              sx={{
                ...itemSx,
                ...(active
                  ? { color: "custom.link", borderBottomColor: "custom.link" }
                  : null),
              }}
            >
              {item.label}
              {item.children ? (
                <Box component="span" aria-hidden="true" sx={caretDownSx()} />
              ) : null}
            </Box>

            {item.children ? (
              <Box
                className="submenu"
                sx={{
                  display: openIndex === index ? "block" : "none",
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  minWidth: "15rem",
                  backgroundColor: "custom.newsprintRaised",
                  border: "1px solid",
                  borderColor: "custom.ruleHairline",
                  borderTop: "2px solid",
                  borderTopColor: "custom.link",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  zIndex: 10,
                }}
              >
                <MenuList disablePadding>
                  {item.children.map((child, childIndex) => (
                    <MenuItem
                      component={Link}
                      key={childIndex}
                      href={child.link ?? "#"}
                      onClick={() => setOpen(null)}
                      aria-current={isCurrent(child.link) ? "page" : undefined}
                      sx={{
                        fontFamily: FONT_UI,
                        fontSize: "0.8125rem",
                        lineHeight: 1.4,
                        py: 0.5,
                        px: 0.75,
                        color: isCurrent(child.link)
                          ? "custom.link"
                          : "custom.ink",
                        borderBottom: "1px dotted",
                        borderColor: "custom.ruleHairline",
                        "&:last-of-type": { borderBottom: "none" },
                        "&:hover": {
                          color: "custom.link",
                          backgroundColor: "custom.newsprint",
                        },
                      }}
                    >
                      {child.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Box>
            ) : null}
          </Box>
        );
      })}
    </>
  );
};

export default DesktopMenu;
